import { useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { DeviceInspector } from "@/features/brewcontrol/device-inspector";
import { parsePlotRefs, serializePlotRefs } from "@/features/brewcontrol/plot-refs";

interface DevicesSearch {
  /** The inspector's filter expression; absent rather than empty when unset. */
  q?: string;
  /** Signals pinned to the plot panel, as `device:signal` pairs. */
  plot?: string;
}

export const Route = createFileRoute("/brewcontrol/$nodeId/devices")({
  component: NodeDevices,
  validateSearch: (search: Record<string, unknown>): DevicesSearch => {
    const out: DevicesSearch = {};
    if (typeof search.q === "string" && search.q.length > 0) out.q = search.q;
    if (typeof search.plot === "string" && search.plot.length > 0) {
      out.plot = search.plot;
    }
    return out;
  },
});

function NodeDevices() {
  const { nodeId } = Route.useParams();
  const { q, plot } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const plotted = useMemo(() => parsePlotRefs(plot), [plot]);

  return (
    <DeviceInspector
      nodeId={nodeId}
      query={q ?? ""}
      onQueryChange={(next) =>
        // `replace` so refining a filter keystroke by keystroke does not bury
        // the previous page under a hundred history entries.
        void navigate({
          search: (old) => ({ ...old, q: next || undefined }),
          replace: true,
        })
      }
      plotted={plotted}
      onPlottedChange={(next) =>
        // Pinning is a deliberate act, so it gets a history entry — Back
        // unpins, which is what a reader expects of a URL-held view.
        void navigate({
          search: (old) => ({ ...old, plot: serializePlotRefs(next) }),
        })
      }
    />
  );
}
